import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { BASE_URL } from "../../app/helper/axiosInstance";
import { ApiConstants } from "../../app/utils/apis/ApiConstants";
import axios from "axios";
import { apiClient } from "../../app/helper/axiosHelper";
import { setUserProfile } from "./ProfileSlice";

// Define types for your state and API responses
interface AuthState {
    otpId: string | null;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string | null;
    data: any;
    access_token: string | null;
    refresh_token: string | null;
    user: any;
    isAuthenticated: boolean;
}

// OTP FLOW DISABLED TEMPORARILY
// interface SendOtpPayload {
//     countryCode: string;
//     phoneNumber: string;
// }

// interface VerifyOtpPayload {
//     countryCode: string;
//     phoneNumber: string;
//     otpId: string;
//     otp: string;
// }

// interface ResendOtpPayload {
//     otpId: string;
// }

interface CheckUserPayload {
    countryCode: string;
    phoneNumber: string;
}

interface RegisterPayload {
    username: string,
    countryCode: string,
    phoneNumber: string
}

// Define the initial state
const initialState: AuthState = {
    otpId: null,
    status: 'idle',
    error: null,
    data: null,
    access_token: null,
    refresh_token: null,
    user: null,
    isAuthenticated: false
};

const getStore = () => {
    const { store } = require("../store");
    return store;
};

// OTP FLOW DISABLED TEMPORARILY — sendOtp, verifyOtp, resendOtp kept for future re-enablement
/*
export const sendOtp = createAsyncThunk(
    'auth/sendOtp',
    async ({ phoneNumber, countryCode }: SendOtpPayload, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${BASE_URL}${ApiConstants.SENDOTP}`, {
                country_code: countryCode,
                phone_number: phoneNumber
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const verifyOtp = createAsyncThunk(
    'auth/verifyOtp',
    async ({ countryCode, phoneNumber, otpId, otp }: VerifyOtpPayload, { rejectWithValue }) => {
        try {
            const response = await apiClient.post(`${BASE_URL}${ApiConstants.LOGIN}`, {
                country_code: countryCode,
                phone_number: phoneNumber,
                otp_id: otpId,
                otp,
            });
            let store = getStore();
            store.dispatch(setUserProfile(response.data.data.user_details))
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

export const resendOtp = createAsyncThunk(
    'auth/resendOtp',
    async ({ otpId }: ResendOtpPayload) => {
        const response = await apiClient.post(`${BASE_URL}${ApiConstants.RESENDOTP}`, {
            otp_id: otpId,
        });
        return response.data;
    }
);
*/

// Checks if a phone number is registered.
// Returns { user_exists: true, user_details, token_details } for existing users.
// Returns { user_exists: false } for new users so the client can show registration.
export const checkUser = createAsyncThunk(
    'auth/checkUser',
    async ({ phoneNumber, countryCode }: CheckUserPayload, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${BASE_URL}${ApiConstants.CHECKUSER}`, {
                country_code: countryCode,
                phone_number: phoneNumber
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

// Async thunk for refreshing the access token
export const refreshAccessToken = createAsyncThunk(
    'auth/refreshAccessToken',
    async (_, { rejectWithValue }) => {
        try {
            const refresh_token = localStorage.getItem('refresh_token');
            if (!refresh_token) {
                throw new Error("No refresh token available");
            }
            const response = await apiClient.post(ApiConstants.REFRESHTOKEN, { refresh_token });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data || 'Failed to refresh token');
        }
    }
);

// registerOTP 
export const register = createAsyncThunk('auth/register', async ({ username, countryCode, phoneNumber }: RegisterPayload, { rejectWithValue }) => {
    try {
        const response = await apiClient.post(`${BASE_URL}${ApiConstants.REGISTER}`, {
            username: username,
            country_code: countryCode,
            phone_number: phoneNumber,
        });
        return response.data;
    } catch (error: any) {
        return rejectWithValue(error.response ? error.response.data : error.message);
    }
});

// Async thunk for logging out
export const logout = createAsyncThunk(
    'auth/logout',
    async (_, { rejectWithValue }) => {
        try {
            const accessToken = localStorage.getItem('access_token');
            const response = await apiClient.get(`${BASE_URL}${ApiConstants.LOGOUT}`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response ? error.response.data : error.message);
        }
    }
);

// Create the auth slice
const authSlices = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        clearToken(state) {
            localStorage.removeItem("access_token");
            localStorage.removeItem("refresh_token");
            state.access_token = null;
            state.refresh_token = null;
            state.isAuthenticated = false;
            state.user = null;
        },
        setToken(state, action: PayloadAction<{ data: { token_details: { access_token: string, refresh_token: string } } }>) {
            state.user = action.payload.data;
            state.access_token = action.payload.data?.token_details.access_token;
            state.refresh_token = action.payload.data?.token_details.refresh_token;
            state.isAuthenticated = true;
            localStorage.setItem("access_token", state.access_token);
            localStorage.setItem("refresh_token", state.refresh_token);
        },
        setUser(state, action: PayloadAction<{ user: any, isAuthUser: boolean }>) {
            state.user = action.payload.user;
            state.isAuthenticated = action.payload.isAuthUser;
        },
        clearUserData: (state) => {
            state.user = null;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // OTP FLOW DISABLED TEMPORARILY — sendOtp / verifyOtp / resendOtp cases kept for future re-enablement
            /*
            .addCase(sendOtp.pending, (state) => { state.status = 'loading'; state.error = null; })
            .addCase(sendOtp.fulfilled, (state, action) => { state.status = 'succeeded'; state.otpId = action.payload.data.otp_id; state.error = null; })
            .addCase(sendOtp.rejected, (state, action) => { state.status = 'failed'; state.error = action.payload as string; })
            .addCase(verifyOtp.pending, (state) => { state.status = 'loading'; state.error = null; })
            .addCase(verifyOtp.fulfilled, (state, action) => {
                state.status = 'succeeded'; state.error = null;
                if (action.payload.success) {
                    state.data = action.payload.data.user_details;
                    state.user = action.payload.data.user_details;
                    state.access_token = action.payload.data.token_details.access_token;
                    state.isAuthenticated = true;
                    state.refresh_token = action.payload.data.token_details.refresh_token;
                    if (state.access_token) localStorage.setItem('access_token', state.access_token);
                    if (state.refresh_token) localStorage.setItem('refresh_token', state.refresh_token);
                }
            })
            .addCase(verifyOtp.rejected, (state, action) => { state.status = 'failed'; state.error = action.payload as string; })
            .addCase(resendOtp.pending, (state) => { state.status = 'loading'; state.error = null; })
            .addCase(resendOtp.fulfilled, (state) => { state.status = 'succeeded'; state.error = null; })
            .addCase(resendOtp.rejected, (state) => { state.status = 'failed'; })
            */
            .addCase(checkUser.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(checkUser.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.error = null;
                if (action.payload.success && action.payload.data?.user_exists) {
                    state.data = action.payload.data.user_details;
                    state.user = action.payload.data.user_details;
                    state.access_token = action.payload.data.token_details.access_token;
                    state.refresh_token = action.payload.data.token_details.refresh_token;
                    state.isAuthenticated = true;
                    if (state.access_token) localStorage.setItem('access_token', state.access_token);
                    if (state.refresh_token) localStorage.setItem('refresh_token', state.refresh_token);
                }
            })
            .addCase(checkUser.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
            })
            .addCase(refreshAccessToken.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(refreshAccessToken.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.access_token = action.payload.data.access_token;
                if (state.access_token) {
                    localStorage.setItem("access_token", state.access_token);
                }
            })
            .addCase(refreshAccessToken.rejected, (state, action) => {
                state.status = 'failed';
            })
            .addCase(register.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(register.fulfilled, (state, action) => {
                state.status = 'succeeded';
                if (action.payload.success) {
                    state.data = action.payload.data.user_details;
                    state.access_token = action.payload.data.token_details.access_token;
                    state.refresh_token = action.payload.data.token_details.refresh_token;
                    if (state.access_token) {
                        localStorage.setItem('access_token', state.access_token);
                    }

                    if (state.refresh_token) {
                        localStorage.setItem('refresh_token', state.refresh_token);
                    }
                }
                state.error = null;
            })
            .addCase(register.rejected, (state, action) => {
                state.status = 'failed';
            })
    }
});

export const { clearToken, setToken, clearUserData, setUser } = authSlices.actions
export const selectAuthStatus = (state: any) => state.auth?.status;
export const selectIsAuth = (state: any) => state.auth?.isAuthenticated;
export const selectGetUser = (state: any) => state.auth?.user;
export const selectAccessToken = (state: any) => state.auth?.access_token;

// OTP FLOW DISABLED TEMPORARILY — re-export commented thunks when OTP is re-enabled:
// export { sendOtp, verifyOtp, resendOtp };

export default authSlices.reducer;